<template>
  <div class="deposit-app">
    <template v-if="showDepositForm">
    <p class="section-label">Please choose your provider</p>

    <!-- Provider: EcoCash (deshabilitado por ahora) -->
    <label v-if="showEcocash" class="provider-card card-disabled" :class="{ selected: selectedProvider === 'ecocash' }">
      <input
        type="radio"
        name="provider"
        value="ecocash"
        v-model="selectedProvider"
        class="radio-input"
      />
      <span class="radio-circle" :class="{ active: selectedProvider === 'ecocash' }"></span>
      <img src="/icons/ecocash.webp" alt="EcoCash" class="provider-logo" />
      <span class="provider-name">EcoCash</span>
      <button type="button" class="info-btn" @click.prevent="onInfo('ecocash')">
        <span class="info-icon">i</span>
      </button>
    </label>

    <!-- Provider: Vodacom (solo si username empieza por 5) -->
    <label v-if="showVodacom" class="provider-card" :class="{ selected: selectedProvider === 'vodacom' }">
      <input
        type="radio"
        name="provider"
        value="vodacom"
        v-model="selectedProvider"
        class="radio-input"
      />
      <span class="radio-circle" :class="{ active: selectedProvider === 'vodacom' }"></span>
      <img src="/icons/vodacom.png" alt="Vodacom" class="provider-logo" />
      <span class="provider-name">Vodacom</span>
      <button type="button" class="info-btn" @click.prevent="onInfo('vodacom')">
        <span class="info-icon">i</span>
      </button>
    </label>

    <!-- Provider: MyWallet (siempre visible) -->
    <label class="provider-card" :class="{ selected: selectedProvider === 'mywallet' }">
      <input
        type="radio"
        name="provider"
        value="mywallet"
        v-model="selectedProvider"
        class="radio-input"
      />
      <span class="radio-circle" :class="{ active: selectedProvider === 'mywallet' }"></span>
      <img src="/icons/mywallet.webp" alt="MyWallet" class="provider-logo" />
      <span class="provider-name">MyWallet</span>
      <button type="button" class="info-btn" @click.prevent="onInfo('mywallet')">
        <span class="info-icon">i</span>
      </button>
    </label>

    <!-- Phone number row -->
    <div class="phone-row">
      <span class="phone-label">Your phone number:</span>
      <span class="phone-value">{{ formattedPhone }}</span>
    </div>

    <hr class="divider" />

    <!-- Amount section -->
    <p class="section-label amount-label">Deposit amount</p>
    <div class="amount-input-wrapper">
      <input
        type="number"
        v-model="amount"
        class="amount-input"
        placeholder="Deposit amount"
        min="50"
      />
      <span class="min-hint">Min 50 LSL</span>
    </div>

    <!-- Promo code (solo si está habilitado por config) -->
    <template v-if="showPromoCode">
      <div class="promo-header">
        <p class="section-label amount-label" style="margin:0">Promo code</p>
        <span class="optional-badge">OPTIONAL</span>
      </div>
      <div class="amount-input-wrapper">
        <input
          type="text"
          v-model="promoCode"
          class="amount-input"
          placeholder="Enter promo code"
          maxlength="50"
          :disabled="isLoading"
          @input="onPromoCodeInput"
          @blur="validatePromoCodeField"
        />
      </div>
      <div v-if="isValidatingPromo" class="promo-status loading">Validating promo code...</div>
      <div v-else-if="promoValidationMessage" class="promo-status" :class="promoValidationType">
        {{ promoValidationMessage }}
      </div>
      <div class="promo-attempts">
        Attempts left: {{ Math.max(0, maxPromoValidationAttempts - promoValidationAttempts) }}
      </div>
    </template>

    <!-- Deposit button -->
    <button
      type="button"
      class="deposit-btn"
      :disabled="isDepositButtonDisabled"
      @click="onDeposit"
    >
      <span v-if="isLoading" class="spinner"></span>
      <span v-else>Deposit</span>
    </button>

    <!-- Error message -->
    <p v-if="errorMessage" class="error-msg">{{ errorMessage }}</p>
    </template>

    <!-- OTP verification (MyWallet) -->
    <div v-if="showOtpForm" class="otp-section">
      <h3 class="otp-title">Enter Authentication Code</h3>
      <p class="otp-hint">Please enter the 5-digit code sent to your phone to complete the deposit.</p>
      <div class="otp-inputs">
        <input
          v-for="(digit, index) in otpDigits"
          :key="index"
          v-model="otpDigits[index]"
          type="text"
          inputmode="numeric"
          maxlength="1"
          class="otp-digit-input"
          :disabled="isOtpLoading"
          @input="handleOtpInput(index, $event)"
          @keydown="handleOtpKeydown(index, $event)"
          @paste="handleOtpPaste($event)"
        />
      </div>
      <button
        class="deposit-btn"
        :disabled="!isOtpComplete || isOtpLoading"
        @click="verifyOtp"
      >
        <span v-if="isOtpLoading" class="spinner"></span>
        <span v-else>Verify Code</span>
      </button>
      <button class="cancel-otp-btn" :disabled="isOtpLoading" @click="cancelOtp">Cancel</button>
    </div>

    <!-- Response (Vodacom / MyWallet) -->
    <div v-if="responseMessage" class="response-box" :class="responseType">
      <p>{{ responseMessage }}</p>
      <button class="deposit-btn retry-btn" @click="resetForm">
        {{ responseType === 'success' ? 'Accept' : 'Try again' }}
      </button>
    </div>
    <!-- Vodacom Instructions Modal -->
    <transition name="modal-slide">
      <div v-if="showVodacomInstructions" class="instructions-overlay" @click.self="showVodacomInstructions = false">
        <div class="instructions-modal">
          <div class="instructions-header">
            <span class="instructions-title">VODACOM INSTANT INSTRUCTIONS</span>
            <button class="close-btn" @click="showVodacomInstructions = false">&times;</button>
          </div>
          <div class="instructions-steps">
            <div class="step-card">
              <div class="step-number">Step 1</div>
              <hr class="step-divider" />
              <p class="step-text">Enter the amount you wish to deposit and click on the <strong>Deposit</strong> button.</p>
            </div>
            <div class="step-card">
              <div class="step-number">Step 2</div>
              <hr class="step-divider" />
              <p class="step-text">Wait for an SMS with the confirmation instructions to arrive on your mobile number <strong>{{ phone }}</strong>.</p>
            </div>
            <div class="step-card">
              <div class="step-number">Step 3</div>
              <hr class="step-divider" />
              <p class="step-text">Complete your transaction by dialing <strong>*224#</strong> and select <strong>'Complete Transaction'</strong> from the M-Pesa menu.</p>
            </div>
            <div class="step-card">
              <div class="step-number">Step 4</div>
              <hr class="step-divider" />
              <p class="step-text">Confirm your transaction.</p>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- MyWallet Instructions Modal -->
    <transition name="modal-slide">
      <div v-if="showMywalletInstructions" class="instructions-overlay" @click.self="showMywalletInstructions = false">
        <div class="instructions-modal">
          <div class="instructions-header">
            <span class="instructions-title">MYWALLET INSTRUCTIONS</span>
            <button class="close-btn" @click="showMywalletInstructions = false">&times;</button>
          </div>
          <div class="instructions-steps">
            <div class="step-card">
              <div class="step-number">Step 1</div>
              <hr class="step-divider" />
              <p class="step-text">Enter the amount you wish to deposit and click on the <strong>Deposit</strong> button.</p>
            </div>
            <div class="step-card">
              <div class="step-number">Step 2</div>
              <hr class="step-divider" />
              <p class="step-text">Wait for the MyWallet email or notification with the OTT code.</p>
            </div>
            <div class="step-card">
              <div class="step-number">Step 3</div>
              <hr class="step-divider" />
              <p class="step-text">Enter the OTT code you received and press <strong>Continue</strong>.</p>
            </div>
            <div class="step-card">
              <div class="step-number">Step 4</div>
              <hr class="step-divider" />
              <p class="step-text">Confirm the transaction and wait for your deposit to be credited to your account.</p>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import common from '@/scripts/common.js';

export default {
  name: 'DepositAppLesotho',

  data() {
    return {
      selectedProvider: '',
      amount: null,
      promoCode: '',
      promoValidationMessage: '',
      promoValidationType: '',
      isValidatingPromo: false,
      isPromoValid: null,
      promoValidationAttempts: 0,
      maxPromoValidationAttempts: 3,
      showPromoCode: false,
      showDepositForm: true,
      showOtpForm: false,
      isOtpLoading: false,
      otpDigits: ['', '', '', '', ''],
      transactionData: null,
      responseMessage: '',
      responseType: '',
      showMywalletInstructions: false,
      showVodacomInstructions: false,
      isLoading: false,
      errorMessage: '',
      username: this.$route.query.username || '',
      phone: this.$route.query.phone || '',
    };
  },

  computed: {
    formattedPhone() {
      const raw = this.phone.replace(/\D/g, '');
      if (!raw) return '—';
      return raw.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1 $2 $3 $4');
    },
    showEcocash() {
      return this.username.startsWith('6');
    },
    showVodacom() {
      return this.username.startsWith('5') || this.username.startsWith('0');
    },
    hasPromoCodeInput() {
      return this.showPromoCode && (this.promoCode || '').trim().length > 0;
    },
    isOtpComplete() {
      return this.otpDigits.every(d => d !== '' && /^\d$/.test(d));
    },
    isDepositButtonDisabled() {
      if (this.isLoading || this.isValidatingPromo) return true;
      if (!this.selectedProvider || !this.amount) return true;
      if (this.hasPromoCodeInput) return this.isPromoValid !== true;
      return false;
    },
  },

  methods: {
    onInfo(provider) {
      if (provider === 'mywallet') {
        this.showMywalletInstructions = true;
        return;
      }
      if (provider === 'vodacom') {
        this.showVodacomInstructions = true;
        return;
      }
      const actions = {
        ecocash: 'btnInfoEcocash',
      };
      window.parent.postMessage({ action: actions[provider] }, '*');
    },

    onPromoCodeInput() {
      this.promoValidationMessage = '';
      this.promoValidationType = '';
      this.isPromoValid = null;
    },

    async validatePromoCodeField() {
      if (!this.showPromoCode) return true;
      const code = (this.promoCode || '').trim();
      if (!code) {
        this.promoValidationMessage = '';
        this.promoValidationType = '';
        this.isPromoValid = null;
        return true;
      }
      if (this.promoValidationAttempts >= this.maxPromoValidationAttempts && this.isPromoValid !== true) {
        this.promoValidationType = 'error';
        this.promoValidationMessage = 'You reached the maximum of 3 promo validation attempts.';
        return false;
      }
      try {
        this.isValidatingPromo = true;
        this.promoValidationMessage = '';
        this.promoValidationAttempts += 1;
        const apiUrl = process.env.VUE_APP_API_URL_GATEWAY;
        const response = await fetch(`${apiUrl}/wsb/system/validate-promo-code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ division: 'DivisionLesotho', player_id: this.username, code }),
        });
        let result = {};
        try { result = await response.json(); } catch (_) { result = {}; }
        if (!response.ok) throw new Error(result.message || result.error || 'Invalid promo code.');
        const isValid =
          result.valid === true ||
          result.is_valid === true ||
          result.code === 0 ||
          result.code === '0' ||
          result.response_code === 'INS-0' ||
          result.data?.response_code === 'INS-0';
        if (!isValid) throw new Error(result.message || result.response_desc || result.data?.response_desc || 'Invalid promo code.');
        this.isPromoValid = true;
        this.promoValidationType = 'success';
        this.promoValidationMessage = result.message || result.response_desc || result.data?.response_desc || 'Promo code is valid.';
        return true;
      } catch (error) {
        this.isPromoValid = false;
        this.promoValidationType = 'error';
        this.promoValidationMessage = error.message || 'Invalid promo code.';
        return false;
      } finally {
        this.isValidatingPromo = false;
      }
    },

    async loadConfig() {
      const promoCodeEnabled = await common.checkConfigLS('promo_code_app');
      this.showPromoCode = promoCodeEnabled === true;
      if (!this.showPromoCode) this.promoCode = '';
    },

    async onDeposit() {
      this.errorMessage = '';
      if (!this.selectedProvider) {
        this.errorMessage = 'Please select a payment provider.';
        return;
      }
      if (!this.amount || Number(this.amount) < 50) {
        this.errorMessage = 'Minimum deposit amount is 50 LSL.';
        return;
      }
      if (this.showPromoCode && this.promoCode && this.isPromoValid !== true) {
        const valid = await this.validatePromoCodeField();
        if (!valid) return;
      }
      this.isLoading = true;
      if (this.selectedProvider === 'vodacom') {
        await this.submitVodacom();
      } else if (this.selectedProvider === 'mywallet') {
        await this.submitMywallet();
      } else {
        window.parent.postMessage(
          {
            action: 'depositRequest',
            provider: this.selectedProvider,
            amount: Number(this.amount),
            username: this.username,
            phone: this.phone,
            promoCode: this.promoCode.trim() || null,
          },
          '*'
        );
        setTimeout(() => { this.isLoading = false; }, 8000);
      }
    },

    async submitVodacom() {
      const data = {
        username: this.username,
        amount: this.amount,
        promo_code: this.showPromoCode ? (this.promoCode.trim() || null) : null,
        origin: 'iframewebsite',
      };
      try {
        const apiUrl = process.env.VUE_APP_API_URL_VODACOM;
        const response = await fetch(`${apiUrl}/api/deposit/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Unknown error occurred');
        if (result.code === 0) {
          this.responseMessage = result.data.response_desc;
          this.responseType = result.data.response_code === 'INS-0' ? 'success' : 'error';
        } else {
          this.responseMessage = 'Deposit failed: ' + result.message;
          this.responseType = 'error';
        }
        this.showDepositForm = false;
      } catch (error) {
        this.responseMessage = error.message;
        this.responseType = 'error';
        this.showDepositForm = false;
      } finally {
        this.isLoading = false;
      }
    },

    async submitMywallet() {
      const data = {
        username: this.username,
        amount: this.amount,
        promo_code: this.showPromoCode ? (this.promoCode.trim() || null) : null,
        origin: 'iframewebsite',
      };
      try {
        const apiUrl = process.env.VUE_APP_API_URL_MY_WALLET;
        const response = await fetch(`${apiUrl}/api/deposit/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Unknown error occurred');
        if (result.code === 0) {
          if (result.data.status_code === 200) {
            this.transactionData = result.data;
            this.showDepositForm = false;
            this.showOtpForm = true;
          } else {
            this.responseMessage = result.data.description || 'Deposit failed.';
            this.responseType = 'error';
            this.showDepositForm = false;
          }
        } else {
          this.responseMessage = 'Deposit failed: ' + result.message;
          this.responseType = 'error';
          this.showDepositForm = false;
        }
      } catch (error) {
        this.responseMessage = error.message;
        this.responseType = 'error';
        this.showDepositForm = false;
      } finally {
        this.isLoading = false;
      }
    },

    async verifyOtp() {
      this.isOtpLoading = true;
      try {
        const apiUrl = process.env.VUE_APP_API_URL_MY_WALLET;
        const verificationData = {
          username: this.username,
          otp_code: this.otpDigits.join(''),
          transactionId: this.transactionData?.transactionId,
          transaction_token: this.transactionData?.transaction_token,
          external_transaction_id: this.transactionData?.external_transaction_id,
          access_token: this.transactionData?.access_token,
        };
        const response = await fetch(`${apiUrl}/api/deposit/verify-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(verificationData),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'OTP verification failed');
        if (result.code === 0) {
          this.responseMessage = 'Deposit Successful! Your deposit has been processed successfully.';
          this.responseType = 'success';
        } else {
          this.responseMessage = 'OTP verification failed: ' + result.message;
          this.responseType = 'error';
        }
        this.showOtpForm = false;
      } catch (error) {
        this.responseMessage = 'Error verifying OTP: ' + error.message;
        this.responseType = 'error';
        this.showOtpForm = false;
      } finally {
        this.isOtpLoading = false;
      }
    },

    async cancelOtp() {
      this.isOtpLoading = true;
      try {
        const apiUrl = process.env.VUE_APP_API_URL_MY_WALLET;
        await fetch(`${apiUrl}/api/deposit/cancel`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: this.username,
            transactionId: this.transactionData?.transactionId,
            transaction_token: this.transactionData?.transaction_token,
            external_transaction_id: this.transactionData?.external_transaction_id,
            access_token: this.transactionData?.access_token,
          }),
        });
      } catch (_) {
        // ignorar error de cancelación
      } finally {
        this.isOtpLoading = false;
        this.showOtpForm = false;
        this.showDepositForm = true;
        this.transactionData = null;
        this.otpDigits = ['', '', '', '', ''];
      }
    },

    handleOtpInput(index, event) {
      const value = event.target.value;
      if (value && !/^\d$/.test(value)) {
        this.otpDigits[index] = '';
        return;
      }
      if (value && index < 4) {
        this.$nextTick(() => {
          const inputs = this.$el.querySelectorAll('.otp-digit-input');
          inputs[index + 1]?.focus();
        });
      }
    },

    handleOtpKeydown(index, event) {
      if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
        this.$nextTick(() => {
          const inputs = this.$el.querySelectorAll('.otp-digit-input');
          inputs[index - 1]?.focus();
        });
      }
    },

    handleOtpPaste(event) {
      event.preventDefault();
      const pasted = (event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 5);
      pasted.split('').forEach((char, i) => { this.otpDigits[i] = char; });
      this.$nextTick(() => {
        const inputs = this.$el.querySelectorAll('.otp-digit-input');
        const nextEmpty = this.otpDigits.findIndex(d => d === '');
        inputs[nextEmpty === -1 ? 4 : nextEmpty]?.focus();
      });
    },

    resetForm() {
      this.showDepositForm = true;
      this.showOtpForm = false;
      this.responseMessage = '';
      this.responseType = '';
      this.amount = null;
      this.promoCode = '';
      this.isPromoValid = null;
      this.promoValidationMessage = '';
      this.promoValidationType = '';
      this.promoValidationAttempts = 0;
      this.otpDigits = ['', '', '', '', ''];
      this.transactionData = null;
    },

    sendHeight() {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ iframeHeight: height }, '*');
    },
  },

  async mounted() {
    await this.loadConfig();
    this.sendHeight();
    window.addEventListener('resize', this.sendHeight);
  },

  updated() {
    this.$nextTick(() => { this.sendHeight(); });
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.sendHeight);
  },
};
</script>

<style scoped>
/* ──────────────────────────────────────────
   Root / Layout
────────────────────────────────────────── */
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

.deposit-app {
  background-color: rgb(24, 34, 54);
  min-height: 100vh;
  padding: 16px 12px 24px;
  box-sizing: border-box;
  font-family: 'Poppins', sans-serif;
  color: #ffffff;
}

/* ──────────────────────────────────────────
   Section labels
────────────────────────────────────────── */
.section-label {
  font-size: 13px;
  font-weight: 500;
  margin: 0 0 8px 2px;
  color: #e0e6f0;
  text-align: left;
}

.amount-label {
  margin-top: 14px;
}

/* ──────────────────────────────────────────
   Provider cards
────────────────────────────────────────── */
.provider-card {
  display: flex;
  align-items: center;
  background-color: #172033;
  border: 1.5px solid #1f2e45;
  border-radius: 8px;
  padding: 9px 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: border-color 0.2s;
  user-select: none;
}

.provider-card.selected {
  border-color: #3a6ea8;
}

.provider-card.card-disabled {
  opacity: 0.35;
  pointer-events: none;
  cursor: not-allowed;
}

/* hide native radio */
.radio-input {
  display: none;
}

/* custom radio circle */
.radio-circle {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #5a7a9a;
  margin-right: 10px;
  flex-shrink: 0;
  position: relative;
  transition: border-color 0.2s;
}

.radio-circle.active {
  border-color: #4a9eff;
}

.radio-circle.active::after {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #4a9eff;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* provider logo */
.provider-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 5px;
  margin-right: 10px;
  flex-shrink: 0;
}

/* provider name */
.provider-name {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  text-align: right;
  color: #d0dce8;
}

/* info button */
.info-btn {
  background: transparent;
  border: 2px solid #5a7a9a;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 10px;
  flex-shrink: 0;
  padding: 0;
  transition: border-color 0.2s;
}

.info-btn:hover {
  border-color: #8aaac8;
}

.info-icon {
  color: #8aaac8;
  font-size: 11px;
  font-style: italic;
  font-weight: 700;
  line-height: 1;
}

/* ──────────────────────────────────────────
   Phone number row
────────────────────────────────────────── */
.phone-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #111827;
  border-radius: 8px;
  padding: 11px 14px;
  margin-top: 4px;
}

.phone-label {
  font-size: 13px;
  color: #8a9db8;
}

.phone-value {
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 0.5px;
}

/* ──────────────────────────────────────────
   Divider
────────────────────────────────────────── */
.divider {
  border: none;
  border-top: 1px solid #1f2e45;
  margin: 12px 0 0;
}

/* ──────────────────────────────────────────
   Amount input
────────────────────────────────────────── */
.amount-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background-color: #172033;
  border: 1.5px solid #1f2e45;
  border-radius: 8px;
  padding: 0 14px;
  height: 46px;
}

.amount-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #e0e6f0;
  font-size: 13px;
  padding: 0;
  min-width: 0;
}

.amount-input::placeholder {
  color: #4a6080;
}

/* remove native number arrows */
.amount-input::-webkit-outer-spin-button,
.amount-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.amount-input[type='number'] {
  -moz-appearance: textfield;
}

.min-hint {
  font-size: 12px;
  color: #4a6080;
  white-space: nowrap;
  margin-left: 8px;
}

/* ──────────────────────────────────────────
   Deposit button
────────────────────────────────────────── */
.deposit-btn {
  width: 100%;
  margin-top: 18px;
  padding: 14px;
  background: linear-gradient(135deg, #ff4d7d, #e8305a);
  border: none;
  border-radius: 50px;
  color: #000000;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
}

.optional-hint {
  font-size: 11px;
  font-weight: 400;
  color: #4a6080;
}

.deposit-btn:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.deposit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.deposit-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ──────────────────────────────────────────
   Spinner
────────────────────────────────────────── */
.spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ──────────────────────────────────────────
   Error message
────────────────────────────────────────── */
.error-msg {
  margin-top: 14px;
  text-align: center;
  color: #ff6b8a;
  font-size: 13px;
}

/* ──────────────────────────────────────────
   Promo code
────────────────────────────────────────── */
.promo-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  margin-bottom: 8px;
}

.optional-badge {
  background-color: #e8305a;
  color: #ffffff;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.promo-status {
  margin-top: 6px;
  font-size: 11px;
  padding: 0 2px;
}

.promo-status.success { color: #4ade80; }
.promo-status.error   { color: #ff6b8a; }
.promo-status.loading { color: #8aaac8; }

.promo-attempts {
  margin-top: 4px;
  font-size: 10px;
  color: #4a6080;
  padding: 0 2px;
}

/* ──────────────────────────────────────────
   Response box (Vodacom)
────────────────────────────────────────── */
.response-box {
  border-radius: 10px;
  padding: 18px 16px;
  margin-top: 8px;
  text-align: center;
}

.response-box p {
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 16px;
  color: #ffffff;
}

.response-box.success {
  background-color: rgba(74, 222, 128, 0.12);
  border: 1px solid #4ade80;
}

.response-box.error {
  background-color: rgba(255, 107, 138, 0.12);
  border: 1px solid #ff6b8a;
}

.retry-btn {
  margin-top: 0;
  padding: 10px 32px;
  width: auto;
  font-size: 13px;
}

/* ──────────────────────────────────────────
   OTP section (MyWallet)
────────────────────────────────────────── */
.otp-section {
  padding: 4px 0;
}

.otp-title {
  font-size: 15px;
  font-weight: 600;
  color: #e0e6f0;
  margin: 0 0 8px;
  font-family: 'Poppins', sans-serif;
}

.otp-hint {
  font-size: 12px;
  color: #8a9db8;
  margin: 0 0 18px;
  text-align: left;
  line-height: 1.5;
}

.otp-inputs {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 18px;
}

.otp-digit-input {
  width: 46px;
  height: 54px;
  background-color: #172033;
  border: 1.5px solid #1f2e45;
  border-radius: 8px;
  color: #e0e6f0;
  font-size: 22px;
  font-weight: 700;
  text-align: center;
  outline: none;
  font-family: 'Poppins', sans-serif;
  transition: border-color 0.2s;
  caret-color: #4a9eff;
}

.otp-digit-input:focus {
  border-color: #4a9eff;
}

.otp-digit-input:disabled {
  opacity: 0.5;
}

.cancel-otp-btn {
  width: 100%;
  margin-top: 10px;
  padding: 12px;
  background: transparent;
  border: 1.5px solid #1f2e45;
  border-radius: 50px;
  color: #8a9db8;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
  font-family: 'Poppins', sans-serif;
}

.cancel-otp-btn:hover:not(:disabled) {
  border-color: #5a7a9a;
  color: #d0dce8;
}

.cancel-otp-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ──────────────────────────────────────────
   MyWallet Instructions Modal
────────────────────────────────────────── */
.instructions-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 16px;
}

.instructions-modal {
  background-color: #172033;
  border-radius: 14px;
  padding: 20px 14px 24px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.instructions-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.instructions-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: #e0e6f0;
}

.close-btn {
  background: transparent;
  border: none;
  color: #8a9db8;
  font-size: 22px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #ffffff;
}

.instructions-steps {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.step-card {
  background-color: #0f1b2d;
  border-radius: 8px;
  padding: 12px 10px;
  border-left: 3px solid #c9952a;
}

.step-number {
  font-size: 12px;
  font-weight: 600;
  color: #e0e6f0;
  margin-bottom: 8px;
}

.step-divider {
  border: none;
  border-top: 1px solid #1f2e45;
  margin: 0 0 10px;
}

.step-text {
  font-size: 11px;
  color: #8a9db8;
  line-height: 1.55;
  margin: 0;
  text-align: left;
}

.step-text strong {
  color: #d0dce8;
  font-weight: 600;
}

/* Modal animation */
.modal-slide-enter-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.modal-slide-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.modal-slide-enter-from,
.modal-slide-leave-to {
  opacity: 0;
}
.modal-slide-enter-from .instructions-modal,
.modal-slide-leave-to .instructions-modal {
  transform: scale(0.95);
}
</style>
